import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataset,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { isEqual } from "lodash";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

function TrackMapPreview() {
  const trackMapPreview = useSelector(
    (state: RootState) => state.trackMap?.trackMapPreview,
    isEqual,
  );

  const chartData = useMemo(() => {
    if (!trackMapPreview) {
      return { datasets: [] };
    }

    const datasets: ChartDataset<"scatter">[] = [];

    // Inner track edge
    if (trackMapPreview.trackEdges?.inner?.lat?.length > 0) {
      const innerData = trackMapPreview.trackEdges.inner.lat.map(
        (lat: number, i: number) => ({
          x: trackMapPreview.trackEdges.inner.lon[i],
          y: lat,
        }),
      );
      datasets.push({
        data: innerData,
        borderColor: "#ffffff",
        backgroundColor: "transparent",
        pointRadius: 0,
        showLine: true,
        borderWidth: 1,
        fill: false,
        order: 10,
      });
    }

    // Outer track edge
    if (trackMapPreview.trackEdges?.outer?.lat?.length > 0) {
      const outerData = trackMapPreview.trackEdges.outer.lat.map(
        (lat: number, i: number) => ({
          x: trackMapPreview.trackEdges.outer.lon[i],
          y: lat,
        }),
      );
      datasets.push({
        data: outerData,
        borderColor: "#ffffff",
        backgroundColor: "transparent",
        pointRadius: 0,
        showLine: true,
        borderWidth: 1,
        fill: false,
      });
    }

    // Turn markings
    trackMapPreview.turnMarkings?.forEach(
      (turn: { name: string; lat: number; lon: number }) => {
        datasets.push({
          label: turn.name,
          data: [{ x: turn.lon, y: turn.lat }],
          pointRadius: 0,
          showLine: false,
        });
      },
    );

    // Loop markings
    trackMapPreview.loopMarkings?.forEach(
      (loop: { name: string; lat: number; lon: number }) => {
        datasets.push({
          label: loop.name,
          data: [{ x: loop.lon, y: loop.lat }],
          pointRadius: 0,
          showLine: false,
        });
      },
    );

    return { datasets };
  }, [trackMapPreview]);

  // Calculate bounds with cosine correction for equal real-world scale
  const bounds = useMemo(() => {
    if (!chartData.datasets.length) {
      return { minLon: 0, maxLon: 1, minLat: 0, maxLat: 1 };
    }

    const allPoints = chartData.datasets
      .flatMap((d) => d.data)
      .filter((p): p is { x: number; y: number } => p !== null);
    if (!allPoints.length) {
      return { minLon: 0, maxLon: 1, minLat: 0, maxLat: 1 };
    }

    const lons = allPoints.map((p) => p.x);
    const lats = allPoints.map((p) => p.y);

    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;

    const cosLat = Math.cos((centerLat * Math.PI) / 180);

    const lonRange = maxLon - minLon;
    const latRange = maxLat - minLat;

    const lonRangeAdjusted = lonRange * cosLat;

    const maxAdjustedRange = Math.max(lonRangeAdjusted, latRange);

    const padding = maxAdjustedRange * 0.05;
    const totalAdjustedRange = maxAdjustedRange + 2 * padding;

    const finalLatRange = totalAdjustedRange;
    const finalLonRange = totalAdjustedRange / cosLat;

    return {
      minLon: centerLon - finalLonRange / 2,
      maxLon: centerLon + finalLonRange / 2,
      minLat: centerLat - finalLatRange / 2,
      maxLat: centerLat + finalLatRange / 2,
    };
  }, [chartData]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 1,
      animation: false as const,
      events: [],
      plugins: {
        datalabels: {
          display: (context: Context) => {
            return context.dataset.data.length === 1;
          },
          color: "#ffffff",
          textStrokeColor: "#2c2c2c",
          textStrokeWidth: 5,
          font: {
            weight: "bold" as const,
            size: 10,
          },

          formatter: (_value: unknown, context: Context) => {
            return context.dataset.label || "";
          },
          anchor: "center" as const,
          align: "center" as const,
          offset: 10,
        },
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          type: "linear" as const,
          min: bounds.minLon,
          max: bounds.maxLon,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
        y: {
          type: "linear" as const,
          min: bounds.minLat,
          max: bounds.maxLat,
          title: { display: false },
          ticks: { display: false },
          grid: { display: false },
          border: { display: false },
        },
      },
    }),
    [bounds],
  );

  const hasTrackData = trackMapPreview?.trackEdges?.inner?.lat?.length > 0;

  return (
    <div className={styles.container}>
      {!hasTrackData && (
        <div className={styles.noDataOverlay}>Track map not available</div>
      )}
      <Scatter data={chartData} options={chartOptions} />
    </div>
  );
}

export default TrackMapPreview;
